import { Theme } from '@mui/material/styles';
import { SxProps } from '@mui/system';
import { brandAlpha, coarsePointerOnly, dialogFormColumn } from '../../../theme/mixins';

export const dialogTitleStyles: SxProps<Theme> = {
    fontWeight: 700,
    pb: 1,
};

/** Lets the fields scroll under a pinned footer -- see `dialogFormColumn`. */
export const dialogFormStyles: SxProps<Theme> = dialogFormColumn;

export const dialogActionsStyles: SxProps<Theme> = {
    px: 3,
    pb: 3,
    pt: 1,
    flexWrap: 'wrap',
    gap: 1,
};

export const descriptionStyles: SxProps<Theme> = {
    color: 'text.secondary',
    mb: 2,
};

export const formAlertStyles: SxProps<Theme> = {
    mb: 2,
};

/**
 * The two things asked once for the whole batch. One column on a phone, two above it.
 *
 * The floor is `min(220px, 100%)` rather than a bare `220px`: a hard floor pushes the dialog
 * sideways the moment it is narrower than that, which full-screen on a 375px phone it is.
 */
export const sharedFieldsStyles: SxProps<Theme> = {
    display: 'grid',
    gridTemplateColumns: {
        xs: '1fr',
        sm: 'repeat(2, minmax(min(220px, 100%), 1fr))',
    },
    gap: 2,
    alignItems: 'start',
};

export const fileListStyles: SxProps<Theme> = {
    display: 'flex',
    flexDirection: 'column',
    gap: 1.5,
    mt: 3,
};

/**
 * One chosen file: what it is called and how big it is, the title it will be filed under,
 * and the way to take it back out. Tinted so each reads as one thing among several.
 */
export const fileRowStyles: SxProps<Theme> = {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    columnGap: 1,
    rowGap: 1,
    alignItems: 'start',
    p: 1.5,
    borderRadius: 2,
    backgroundColor: brandAlpha('primary', 0.05),
};

/** The file's own name and size, across the top of its row. Long names are cut, not wrapped. */
export const fileNameStyles: SxProps<Theme> = {
    gridColumn: '1 / -1',
    color: 'text.secondary',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
};

export const pickRowStyles: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 2,
    mt: 2,
};

/**
 * Drawn only where a finger is the pointer. `capture` means something on a phone or a
 * tablet; a desktop browser ignores it and opens the file picker, so there this would be a
 * second way to choose a file, labelled as something else.
 */
export const takePhotoButtonStyles: SxProps<Theme> = {
    display: 'none',
    ...coarsePointerOnly({ display: 'inline-flex' }),
};

export const pickHintStyles: SxProps<Theme> = {
    color: 'text.secondary',
};

export const progressStyles: SxProps<Theme> = {
    mt: 2,
};

/** Pushes the hint to the left of the buttons, and lets it wrap under them on a phone. */
export const blockedHintStyles: SxProps<Theme> = {
    flex: '1 1 200px',
    color: 'text.secondary',
};
